import { useTranslator } from '@Localization';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { GTable, IGColumnProps } from '../../../../../../__legacy__/components/table/GTable';
import { IGSubColumnProps } from '../../../../../../__legacy__/components/table/IGColumnProps';
import { enumDirectionToStringOrder, stringOrderToEnumDirection } from '../../../../../../__legacy__/components/table/TableHelpers';
import { formatTimeSpan, toLocaleDateString } from '../../../../../../__legacy__/helpers/date.helpers';
import { SortingDirection } from '../../../../../../__legacy__/models/sortingDirection.model';
import { IDailyGeneralStats, IGeneralStats } from '../models/GeneralStats';

interface IProps {
  items: IGeneralStats[];
  orderBy: string;
  orderDirection: SortingDirection | undefined;
  onSortChange: (orderBy: string, orderDirection: SortingDirection | undefined) => void;
}

export const GeneralStatsTable = observer((props: IProps): ReactElement => {
  const { t } = useTranslator();
  const { orderBy, orderDirection, onSortChange } = props;
  const renderTimeSpan = (timeSpan: string): ReactElement => <span>{formatTimeSpan(timeSpan)}</span>;

  const columns: IGColumnProps<IGeneralStats>[] = [
    {
      sortBy: 'email',
      headerName: t('projects.details.stats.general.headers.email'),
      cellClassName: 'stats-cell-bold',
      renderer: (x) => x.email,
    },
    {
      sortBy: 'submitted',
      headerName: t('projects.details.stats.general.headers.annotated'),
      headerTooltip: t('projects.details.stats.general.tooltips.annotated'),
      renderer: (x) => x.submitted,
      cellClassName: 'stats-cell-bold',
    },
    {
      sortBy: 'reviewed',
      headerName: t('projects.details.stats.general.headers.reviewed'),
      headerTooltip: t('projects.details.stats.general.tooltips.reviewed'),
      renderer: (x) => x.reviewed,
      cellClassName: 'stats-cell-bold',
    },
    {
      sortBy: 'edited',
      headerName: t('projects.details.stats.general.headers.edited'),
      headerTooltip: t('projects.details.stats.general.tooltips.edited'),
      renderer: (x) => x.edited,
      cellClassName: 'stats-cell-bold',
    },
    {
      sortBy: 'annotationWorkTime',
      headerName: t('projects.details.stats.general.headers.annotation_work_time'),
      headerTooltip: t('projects.details.stats.general.tooltips.annotation_work_time'),
      renderer: ({ annotationWorkTime }) => renderTimeSpan(annotationWorkTime),
      cellClassName: 'stats-cell-bold',
    },
    {
      sortBy: 'reviewWorkTime',
      headerName: t('projects.details.stats.general.headers.review_work_time'),
      headerTooltip: t('projects.details.stats.general.tooltips.review_work_time'),
      renderer: ({ reviewWorkTime }) => renderTimeSpan(reviewWorkTime),
      cellClassName: 'stats-cell-bold',
    },
    {
      sortBy: 'editedWorkTime',
      headerName: t('projects.details.stats.general.headers.edit_work_time'),
      headerTooltip: t('projects.details.stats.general.tooltips.edit_work_time'),
      renderer: ({ editedWorkTime }) => renderTimeSpan(editedWorkTime),
      cellClassName: 'stats-cell-bold',
    },
    {
      sortBy: 'workTime',
      headerName: t('projects.details.stats.general.headers.total_work_time'),
      headerTooltip: t('projects.details.stats.general.tooltips.total_work_time'),
      renderer: ({ workTime }) => renderTimeSpan(workTime),
      cellClassName: 'stats-cell-bold',
    },
  ];

  const subColumns: IGSubColumnProps<IDailyGeneralStats>[] = [
    {
      renderer: (item) => <>{toLocaleDateString(item.day)}</>,
      cellClassName: 'stats-cell-bold',
    },
    { renderer: (item) => item.submitted },
    { renderer: (item) => item.reviewed },
    { renderer: (item) => item.edited },
    {
      renderer: (item) => renderTimeSpan(item.annotationWorkTime),
    },
    {
      renderer: (item) => renderTimeSpan(item.reviewWorkTime),
    },
    {
      renderer: (item) => renderTimeSpan(item.editedWorkTime),
    },
    {
      renderer: (item) => renderTimeSpan(item.workTime),
    },
  ];

  const items = props.items.map((i) => ({
    ...i,
    subRows: i.dailyGeneralStats.map((d) => ({ ...d, id: d.day })),
  }));

  const handleSortChange = (orderBy: string, orderDirection: string): void => {
    onSortChange(orderBy, stringOrderToEnumDirection(orderDirection));
  };

  return (
    <GTable
      isLoading={false}
      columns={columns}
      items={items}
      subColumns={subColumns}
      noItemsPlaceholder={t('projects.details.stats.no_statistics_have_been_generated_yet')}
      onSortChanged={handleSortChange}
      sortingModel={[{ orderBy, orderType: enumDirectionToStringOrder(orderDirection) }]}
    />
  );
});
